import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { Panel } from '../../components/layout/Panel';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { ReferencedWorkDTO, WorkIsPartOfDTO, WorkIsPartOfOtherDTO } from '../../../../api/core';
import { getDefaultWorkIsPartOfOtherModel, getDefaultWorkPart } from '../../../../Settings/emptyModels';
import React, { ReactElement, ReactNode } from 'react';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledWorkSuggester } from '../../../components/form/LabelledWorkSuggester';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Fieldset } from '../../components/layout/Fieldset';
import { PubtypeUtils } from '../../../../util/pubtype-utils';
import { FieldSettings } from '../../../../util/types';

export const IsPartOfPage = (props: Props): ReactElement => {
  return <Block title={`work.header.ispartof`}>
    <PermissionGate always={false} entity={props.permissionEntity} field="work_is_part_ofs">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'isPartOf'} className={'col-12'} title={'work.attr.isPartOf'}>
          <MultiInputControls<WorkIsPartOfDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workIsPartOfs}
            addNew={(rows: WorkIsPartOfDTO[]): number => rows.push(getDefaultWorkPart())}
            onChange={(parts: WorkIsPartOfDTO[]): void => props.setField('workIsPartOfs', parts)}
            renderLines={(parent: WorkIsPartOfDTO, onLineChange: (newValues: WorkIsPartOfDTO) => void, actions: Actions, idx: number): ReactNode => {
              const fieldSettings: FieldSettings = parent?.referencedWork?.pubtype ?
                PubtypeUtils.getIsPartOfFields(props.pubtypeGroup, PubtypeUtils.getGroupByPubtype(parent.referencedWork.pubtype)) :
                {pageFirst: false, pageLast: false, volume: false, issue: false, articleId: false};
              return <Fieldset className={'flex flex-column gap-5'}>
                <InputGroup
                  delete={{
                    action: actions.delete,
                    disabled: !checkPermission().canWrite,
                  }}
                >
                  <LabelledWorkSuggester
                    id={`isPartOf-${idx}-work`}
                    autoFocus={props.autoFocus}
                    value={parent.referencedWork ? [parent.referencedWork] : []}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.referenced_work}
                    permissions={checkPermission('referenced_work')}
                    onChange={(referencedWorks: ReferencedWorkDTO[]): void => onLineChange({
                      referencedWork: referencedWorks.length > 0 ? referencedWorks.pop() : undefined,
                      issue: undefined,
                      pageFirst: undefined,
                      pageLast: undefined,
                      articleId: undefined,
                      volume: undefined,
                    })}
                    label={'work.attr.part.attr.referencedWork'}
                    multiple={false}
                  />
                </InputGroup>
                <InputGroup>
                  <LabelledInputText
                    id={`isPartOf-${idx}-volume`}
                    value={parent.volume || ''}
                    permissions={checkPermission('volume')}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.volume}
                    onChange={(volume: string): void => onLineChange({...parent, volume})}
                    label={'work.attr.part.attr.volume'}
                    disabled={!fieldSettings.volume}
                  />
                  <LabelledInputText
                    id={`isPartOf-${idx}-issue`}
                    value={parent.issue || ''}
                    permissions={checkPermission('issue')}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.issue}
                    onChange={(issue: string): void => onLineChange({...parent, issue})}
                    label={'work.attr.part.attr.issue'}
                    disabled={!fieldSettings.issue}
                  />
                  <LabelledInputText
                    id={`isPartOf-${idx}-pageFirst`}
                    value={parent.pageFirst || ''}
                    permissions={checkPermission('page_first')}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.page_first}
                    onChange={(pageFirst: string): void => onLineChange({...parent, pageFirst})}
                    label={'work.attr.part.attr.pageFirst'}
                    disabled={!fieldSettings.pageFirst}
                  />
                  <LabelledInputText
                    id={`isPartOf-${idx}-pageLast`}
                    value={parent.pageLast || ''}
                    permissions={checkPermission('page_last')}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.page_last}
                    onChange={(pageLast: string): void => onLineChange({...parent, pageLast})}
                    label={'work.attr.part.attr.pageLast'}
                    disabled={!fieldSettings.pageLast}
                  />
                  <LabelledInputText
                    id={`isPartOf-${idx}-articleId`}
                    value={parent.articleId || ''}
                    permissions={checkPermission('article_id')}
                    validation={props.validation?.root?.fields?.work_is_part_ofs.children[idx].fields.article_id}
                    onChange={(articleId: string): void => onLineChange({...parent, articleId})}
                    label={'work.attr.part.attr.articleId'}
                    disabled={!fieldSettings.articleId}
                  />
                </InputGroup>
              </Fieldset>;
            }}
          />
        </Panel>
      }
    </PermissionGate>

    <PermissionGate always={false} entity={props.permissionEntity} field="work_is_part_of_others">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'isPartOfOther'} className={'col-12'} title={'work.attr.isPartOfOther'}>
          <MultiInputControls<WorkIsPartOfOtherDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workIsPartOfOthers}
            addNew={(rows: WorkIsPartOfDTO[]): number => rows.push(getDefaultWorkIsPartOfOtherModel())}
            onChange={(parts: WorkIsPartOfDTO[]): void => props.setField('workIsPartOfOthers', parts)}
            renderLines={(parent: WorkIsPartOfOtherDTO, onLineChange: (newValues: WorkIsPartOfOtherDTO) => void, actions: Actions, idx: number): ReactNode => {
              return <Fieldset className={'flex flex-column gap-5'}>
                <InputGroup
                  delete={{
                    action: actions.delete,
                    disabled: !checkPermission().canWrite,
                  }}
                >
                  <LabelledInputText
                    id={`isPartOf-${idx}-work`}
                    value={parent.name || ''}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.name}
                    permissions={checkPermission('name')}
                    onChange={(name: string): void => onLineChange({...parent, name})}
                    label={'work.attr.part.attr.name'}
                  />
                </InputGroup>
                <InputGroup>
                  <LabelledInputText
                    id={`isPartOfOther-${idx}-volume`}
                    value={parent.volume || ''}
                    permissions={checkPermission('volume')}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.volume}
                    onChange={(volume: string): void => onLineChange({...parent, volume})}
                    label={'work.attr.part.attr.volume'}

                  />
                  <LabelledInputText
                    id={`isPartOfOther-${idx}-issue`}
                    value={parent.issue || ''}
                    permissions={checkPermission('issue')}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.issue}
                    onChange={(issue: string): void => onLineChange({...parent, issue})}
                    label={'work.attr.part.attr.issue'}
                  />
                  <LabelledInputText
                    id={`isPartOfOther-${idx}-pageFirst`}
                    value={parent.pageFirst || ''}
                    permissions={checkPermission('page_first')}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.page_first}
                    onChange={(pageFirst: string): void => onLineChange({...parent, pageFirst})}
                    label={'work.attr.part.attr.pageFirst'}
                  />
                  <LabelledInputText
                    id={`isPartOfOther-${idx}-pageLast`}
                    value={parent.pageLast || ''}
                    permissions={checkPermission('page_last')}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.page_last}
                    onChange={(pageLast: string): void => onLineChange({...parent, pageLast})}
                    label={'work.attr.part.attr.pageLast'}
                  />
                  <LabelledInputText
                    id={`isPartOfOther-${idx}-articleId`}
                    value={parent.articleId || ''}
                    permissions={checkPermission('article_id')}
                    validation={props.validation?.root?.fields?.work_is_part_of_others.children[idx].fields.article_id}
                    onChange={(articleId: string): void => onLineChange({...parent, articleId})}
                    label={'work.attr.part.attr.articleId'}
                  />
                </InputGroup>
              </Fieldset>;
            }}
          />
        </Panel>
      }
    </PermissionGate>
  </Block>;
};
